import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from './SEO';
import { Sparkles, Globe, Heart, Shield, ArrowRight, Quote } from 'lucide-react';
import aboutHero from '../assets/about_hero_v3.webp';
import goaDest from '../assets/goa_dest_v2.webp';
import rajasthanDest from '../assets/rajasthan_dest_v2.webp';
import culturalIndia from '../assets/cultural_india_v1.webp';
import destinationsHeroBg from '../assets/destinations_hero_bg.webp';
import heroBg from '../assets/hero_bg.webp';
import mapBadge from '../assets/map_badge.webp';

const indianNatureHero = "https://images.pexels.com/photos/13691355/pexels-photo-13691355.jpeg?auto=compress&cs=tinysrgb&w=2000";

const About = () => {
  return (
    <div className="bg-white min-h-screen pb-12 font-sans">
      <SEO
        title="Our Story & Heritage"
        description="For over 25 years, Sunshine Travels has been a trusted companion in travel. Meet our founder and explore our heritage of quality tours, school trips, and corporate travels."
        canonicalPath="/about-us"
        noindex={true}
      />
      {/* Hero Section */}
      <section className="relative h-[45vh] md:h-[55vh] flex items-center overflow-hidden pt-20 md:pt-24">
        <div className="absolute inset-0 z-0">
          <img
            src={aboutHero}
            alt="Amalfi Coast at Sunset"
            className="w-full h-full object-cover hd-image"
          />
          <div className="absolute inset-0 bg-slate-900/40"></div>
          {/* Bottom White Fade */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10 flex flex-col md:flex-row items-center justify-between gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl space-y-4 md:space-y-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500 text-white text-[9px] md:text-[10px] font-bold uppercase tracking-[0.2em] shadow-xl shadow-orange-500/20">
              <Sparkles size={12} /> Celebrating 25 Years
            </span>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white leading-snug tracking-wide">
              Sunshine Travels <br />
              <span className="text-orange-400">& Holidays.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 font-medium max-w-2xl">
              Celebrating 25 Years of Handcrafting Journeys
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <a
              href="https://www.google.com/maps/place/Sunshine+Travels+and+Holidays/@13.3552035,74.7619176,13z/data=!4m6!3m5!1s0x3bbca51cac348ccd:0x1f114dc0c7a1352c!8m2!3d13.351605!4d74.7875424!16s%2Fg%2F11gnsd3jpb?entry=ttu&g_ep=EgoyMDI2MDUxMi4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="block relative"
            >
              <img
                src={mapBadge}
                alt="Our Office Location"
                className="relative z-10 w-72 h-auto rounded-[2rem] shadow-2xl border-4 border-white/20"
              />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Narrative Section */}
      <section className="py-16 md:py-24 container mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
          <div className="lg:w-1/2 space-y-6 md:space-y-8 order-2 lg:order-1">
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-[2px] bg-orange-500"></div>
                <span className="text-orange-500 font-black uppercase tracking-[0.3em] text-[9px] md:text-[10px]">Our Story</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-serif font-bold text-slate-900 leading-snug tracking-wide">
                Some journeys begin with a passport.<br />
                <span className="text-orange-500">Ours began with a vision.</span>
              </h2>
              <div className="space-y-4 text-slate-700 text-base md:text-lg leading-relaxed font-medium">
                <p>
                  Founded in 2001, Sunshine Travels began as a passion driven travel venture envisioned from home and built through dedication, expertise, and personalized service.
                </p>
                <p>
                  What began from a home based office to a centrally located beautiful office in the vibrant International University town of Manipal, became something far greater, a bridge between people, cultures, emotions, and unforgettable experiences.
                </p>
                <p className="hidden md:block">
                  For 25 years, Sunshine has not simply planned holidays. We have witnessed first honeymoons become family traditions. We have watched children who once travelled with their parents return years later with families of their own. We have celebrated stories, milestones, reunions, discoveries, and moments that became memories for a lifetime.
                </p>
                <p>
                  Today, Sunshine Travels proudly stands as a trusted travel companion for clients across the globe, built not merely on itineraries, but on relationships, trust, and the joy of creating experiences that truly matter.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2 relative order-1 lg:order-2 flex justify-center items-center mt-8 lg:mt-0 w-full overflow-hidden md:overflow-visible py-12 md:py-0">
            <div className="relative w-full max-w-[400px] md:max-w-[500px] h-[350px] md:h-[550px]">
              {/* Image 3 (Back) */}
              <motion.div
                initial={{ opacity: 0, rotate: -15, x: -40, y: 20 }}
                whileInView={{ opacity: 1, rotate: -12, x: -20, y: 0 }}
                viewport={{ once: true }}
                className="absolute top-0 left-0 bg-white p-2 md:p-3 pb-8 md:pb-12 shadow-xl rounded-sm w-[180px] md:w-[320px] z-10 border border-slate-200 hover:z-40 hover:scale-105 transition-transform duration-500 cursor-pointer"
              >
                <img
                  src={indianNatureHero}
                  alt="Kerala Backwaters"
                  className="w-full h-[150px] md:h-[300px] object-cover"
                />
                <div className="absolute bottom-2 md:bottom-4 left-3 md:left-6">
                  <p className="font-handwriting text-slate-400 text-sm md:text-lg">Kerala Backwaters</p>
                </div>
              </motion.div>

              {/* Image 2 (Middle) */}
              <motion.div
                initial={{ opacity: 0, rotate: 15, x: 40, y: -20 }}
                whileInView={{ opacity: 1, rotate: 12, x: 50, y: -10, md: { x: 80, y: -20 } }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="absolute top-6 right-2 md:top-10 md:right-[-32px] bg-white p-2 md:p-3 pb-8 md:pb-12 shadow-2xl rounded-sm w-[160px] md:w-[300px] z-20 border border-slate-200 hover:z-40 hover:scale-105 transition-transform duration-500 cursor-pointer"
              >
                <img
                  src={goaDest}
                  alt="Coastal Bliss"
                  className="w-full h-[130px] md:h-[260px] object-cover"
                />
                <div className="absolute bottom-2 md:bottom-4 left-3 md:left-6">
                  <p className="font-handwriting text-slate-400 text-sm md:text-lg">Coastal Bliss</p>
                </div>
              </motion.div>

              {/* Image 1 (Front) */}
              <motion.div
                initial={{ opacity: 0, rotate: -5, y: 40 }}
                whileInView={{ opacity: 1, rotate: -4, y: 30, x: 10, md: { y: 60, x: 20 } }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="absolute top-12 left-8 md:top-20 md:left-20 bg-white p-2 md:p-4 pb-10 md:pb-16 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.3)] md:shadow-[0_35px_60px_-15px_rgba(0,0,0,0.3)] rounded-sm w-[200px] md:w-[360px] z-30 border border-slate-100 hover:scale-105 transition-transform duration-500 cursor-pointer"
              >
                <img
                  src={rajasthanDest}
                  alt="Royal Heritage"
                  className="w-full h-[180px] md:h-[320px] object-cover"
                />
                <div className="absolute bottom-3 md:bottom-6 left-4 md:left-8">
                  <p className="font-handwriting text-slate-500 text-sm md:text-xl font-bold tracking-wide">Royal Heritage</p>
                </div>
              </motion.div>

              {/* Image 4 (New - Cultural India) */}
              <motion.div
                initial={{ opacity: 0, rotate: 10, x: 20, y: 60 }}
                whileInView={{ opacity: 1, rotate: 8, x: 40, y: 80, md: { x: 120, y: 100 } }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute top-20 right-[-20px] md:top-32 md:right-[-60px] bg-white p-2 md:p-3 pb-8 md:pb-12 shadow-2xl rounded-sm w-[150px] md:w-[280px] z-15 border border-slate-200 hover:z-40 hover:scale-105 transition-transform duration-500 cursor-pointer hidden sm:block"
              >
                <img
                  src={culturalIndia}
                  alt="Spiritual India"
                  className="w-full h-[120px] md:h-[240px] object-cover"
                />
                <div className="absolute bottom-2 md:bottom-4 left-3 md:left-6">
                  <p className="font-handwriting text-slate-400 text-sm md:text-lg">Spiritual Heritage</p>
                </div>
              </motion.div>

              <div className="absolute -top-6 -left-6 md:-top-10 md:-left-10 w-20 h-20 md:w-32 md:h-32 opacity-10 pointer-events-none rotate-12 z-0">
                <Globe size={80} className="text-orange-500 md:block hidden" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Behind the Smile - Founder Section */}
      <section className="py-16 md:py-24 bg-orange-50/50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, #f97316 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-6xl mx-auto bg-white rounded-[2rem] md:rounded-[3rem] overflow-hidden shadow-2xl border border-orange-100/50">
            <div className="flex flex-col lg:flex-row items-center">
              {/* Image Section */}
              <div className="lg:w-1/2 p-6 md:p-16 flex justify-center bg-orange-50/30">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="relative group"
                >
                  <div className="absolute -inset-4 bg-orange-500/10 rounded-[2.5rem] blur-2xl group-hover:bg-orange-500/20 transition-all duration-700"></div>
                  <img
                    src="/founder.jpg"
                    alt="The Story Behind The Smile"
                    className="relative z-10 w-full max-w-[280px] md:max-w-[380px] h-auto rounded-[1.5rem] md:rounded-[2rem] shadow-2xl ring-4 md:ring-8 ring-white"
                  />
                  <div className="absolute -bottom-6 -right-6 bg-slate-900 text-white px-6 py-3 rounded-2xl shadow-xl z-20 hidden md:block">
                    <p className="font-bold uppercase tracking-widest text-[9px]">Sunshine Travels Founder</p>
                  </div>
                </motion.div>
              </div>

              {/* Text Content */}
              <div className="lg:w-1/2 p-8 lg:p-20 space-y-6 md:space-y-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-[2px] bg-orange-500"></div>
                    <span className="text-orange-500 font-black uppercase tracking-[0.3em] text-[9px] md:text-[10px]">The Founder's Journey</span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-serif font-bold text-slate-900 leading-tight tracking-wide">
                    The Story <br />
                    <span className="italic text-orange-500">Behind</span> the Smile
                  </h2>
                </div>

                <div className="space-y-6">
                  <p className="text-slate-800 text-lg md:text-2xl font-serif leading-relaxed italic border-l-4 border-orange-500 pl-4 md:pl-8">
                    "Founded in 2001, we've spent 25 years helping thousands travel hassle-free.
                    We're a woman-led, multilingual, and empathy driven team... always just one call away!"
                  </p>

                  <div className="space-y-4 text-slate-600 text-base md:text-lg leading-relaxed">
                    <p>
                      Our venture was born from a simple belief: that travel should be about more than just moving from one place to another. It's about the people you meet, the cultures you experience, and the joy of discovery.
                    </p>
                  </div>
                </div>

                <div className="pt-4">
                  <div className="inline-flex items-center gap-3 px-6 py-3 bg-slate-900 text-white rounded-full font-bold uppercase tracking-widest text-[9px] md:text-[10px] hover:bg-orange-500 transition-colors shadow-lg shadow-slate-900/10">
                    <Heart size={14} className="text-orange-500" /> Our Heart & Soul
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-16 md:py-24 bg-slate-900 relative overflow-hidden text-white">
        <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] bg-orange-500/10 rounded-full blur-[120px] -mr-32 -mt-32 md:-mr-64 md:-mt-64"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Vision */}
            <div className="space-y-6 md:space-y-8">
              <div className="space-y-4">
                <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[9px] md:text-[10px]">The Future</span>
                <h2 className="text-3xl md:text-5xl font-serif font-bold tracking-wide leading-tight md:leading-snug">
                  OUR <span className="text-orange-400">VISION.</span>
                </h2>
              </div>
              <p className="text-lg md:text-xl font-bold leading-relaxed border-l-4 border-orange-500 pl-4 md:pl-6 italic">
                To become a globally trusted name in experiential travel, crafting soulful, immersive, and deeply personalised journeys that connect people to the world, to cultures, and ultimately, to themselves.
              </p>
              <div className="space-y-4 text-slate-300 font-medium leading-relaxed text-base md:text-lg">
                <p>We believe travel is never just about reaching a destination.</p>
                <ul className="space-y-2 list-disc list-inside">
                  <li>It is about standing before a landscape that leaves you speechless.</li>
                  <li>Sharing a meal with strangers who become friends.</li>
                  <li>Walking through centuries of culture and history.</li>
                  <li>Discovering unfamiliar places that somehow feel like home.</li>
                </ul>
                <p className="pt-4 text-white font-bold">
                  At Sunshine, we envision a future where every journey is thoughtfully designed around human emotion, curiosity, and meaningful connection.
                </p>
                <p className="text-orange-400 font-bold italic">
                  Because the world is too beautiful to be experienced in ordinary ways.
                </p>
              </div>
            </div>

            {/* Mission */}
            <div className="space-y-6 md:space-y-8">
              <div className="space-y-4">
                <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[9px] md:text-[10px]">Our Purpose</span>
                <h2 className="text-3xl md:text-5xl font-serif font-bold tracking-wide leading-tight md:leading-snug">
                  OUR <span className="text-orange-400">MISSION.</span>
                </h2>
              </div>
              <div className="space-y-4 md:space-y-6">
                {[
                  "To create journeys filled with warmth, authenticity, and unforgettable moments.",
                  "To bring the world closer through thoughtfully curated holidays and cultural experiences.",
                  "To design travel experiences that inspire joy, discovery, connection, and lifelong memories.",
                  "To serve every traveller with sincerity, integrity, passion, and personalised care.",
                  "To continue building relationships that transcend borders, generations, and time itself."
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-orange-500/20 flex items-center justify-center shrink-0 mt-1">
                      <div className="w-2 md:w-2.5 h-2 md:h-2.5 rounded-full bg-orange-500"></div>
                    </div>
                    <p className="text-slate-300 text-base md:text-lg font-medium leading-relaxed">{item}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Special */}
      <section className="py-16 md:py-24 bg-slate-50 border-y border-slate-100">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20 space-y-4">
            <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[9px] md:text-[10px]">The Sunshine Difference</span>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-slate-900 tracking-wide leading-snug">
              What Makes <br />
              <span className="text-orange-500">Sunshine Special.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[
              "25 years of trust, passion, and heartfelt service",
              "Personalised journeys handcrafted with care and emotion",
              "Travellers and families from across the globe",
              "Expertise across cultural, leisure, luxury, family, and corporate travel",
              "Three generations of families travelling with us",
              "Deep understanding of destinations, people, and meaningful experiences",
              "A timeless belief that travel has the power to change lives forever"
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white p-8 rounded-[2rem] shadow-lg border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <Sparkles className="text-orange-500 mb-4" size={24} />
                <p className="text-slate-800 font-bold leading-relaxed">{feature}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 md:py-32 container mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20 space-y-4">
          <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[9px] md:text-[10px]">Our Foundation</span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-slate-900 tracking-wide leading-snug">
            Our Core <span className="text-orange-500">Values.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-12">
          {[
            { title: "Cultural Connection", icon: Globe, desc: "Every destination carries a soul, a story, and a legacy waiting to be experienced." },
            { title: "Human Relationships", icon: Heart, desc: "Our travellers are never just clients. They become part of the Sunshine family." },
            { title: "Authentic Experiences", icon: Sparkles, desc: "We go beyond itineraries to create journeys that feel personal, emotional, and unforgettable." },
            { title: "Trust & Transparency", icon: Shield, desc: "For 25 years, our foundation has been honesty, commitment, and genuine care." },
            { title: "Passion for Travel", icon: ArrowRight, desc: "Travel is not simply what we do. It is who we are." }
          ].map((value, index) => (
            <div key={value.title} className="text-center space-y-4 group">
              <div className="w-16 h-16 md:w-20 md:h-20 mx-auto bg-slate-50 rounded-2xl flex items-center justify-center text-slate-900 group-hover:bg-orange-500 group-hover:text-white transition-all duration-300 shadow-sm">
                <value.icon size={28} className="md:w-8 md:h-8" />
              </div>
              <h3 className="font-black text-slate-900 uppercase tracking-tight text-xs md:text-sm">{value.title}</h3>
              <p className="text-[13px] md:text-sm font-medium text-slate-600 leading-relaxed px-4 md:px-0">{value.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Quote Section */}
      <section className="relative py-24 md:py-32 text-center px-6 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={heroBg}
            alt="Journey Landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/75"></div>
        </div>

        <div className="max-w-4xl mx-auto space-y-6 md:space-y-8 relative z-10">
          <Quote size={40} className="text-orange-500 mx-auto opacity-80 md:w-12 md:h-12" />
          <h2 className="text-2xl md:text-5xl font-serif font-bold text-white tracking-wide leading-snug drop-shadow-2xl px-2">
            Designing personalised journeys that turn every trip into a story worth living.
          </h2>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 px-6">
        <div className="max-w-6xl mx-auto bg-slate-900 rounded-[2rem] md:rounded-[3rem] p-10 md:p-20 flex flex-col md:flex-row items-center justify-between gap-10 md:gap-12 text-white relative overflow-hidden shadow-2xl group">
          <div className="absolute inset-0 z-0">
            <img
              src={destinationsHeroBg}
              className="w-full h-full object-cover transition-transform duration-[5s] group-hover:scale-110 opacity-100"
              alt="Ready for your trip"
            />
            <div className="absolute inset-0 bg-slate-900/60"></div>
          </div>
          <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none z-0" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>

          <div className="space-y-4 max-w-xl text-center md:text-left relative z-10">
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-white tracking-wide">Ready for your trip?</h2>
            <p className="text-slate-200 text-base md:text-lg font-bold leading-relaxed">
              Join thousands of happy travelers who have seen the world with Sunshine Travels.
            </p>
          </div>

          <Link to="/contact">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-orange-500 text-white px-8 md:px-10 py-4 md:py-5 rounded-full font-black shadow-xl flex items-center gap-3 relative z-10 hover:bg-orange-600 transition-all uppercase tracking-[0.2em] text-[10px] md:text-xs"
            >
              Contact Us <ArrowRight size={20} />
            </motion.button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
